<template>
  <section class="ai-visibility-questions-tab">
    <loading-state v-if="loading" label="Loading questions data..." />
    <error-state
      v-else-if="loadError"
      :message="loadError"
      retry-label="Retry questions"
      @retry="loadQuestions"
    />
    <empty-state
      v-else-if="!displayRows.length"
      title="No questions data yet"
      description="Run this AI Visibility tracker to see tracked question data."
    />
    <div v-else class="ai-visibility-questions-tab__content">
      <question-summary-bar :summary="summary" />
      <questions-toolbar
        :active-filter="activeFilter"
        :filter-counts="filterCounts"
        :search="search"
        @update:filter="handleFilterChange"
        @update:search="handleSearchUpdate"
      />
      <div
        v-if="hasQuestionIdFilter"
        class="ai-visibility-questions-tab__citation-filter"
      >
        <div>
          <strong>Filtered by citation</strong>
          <span>{{ citationFilterSummary }}</span>
          <span
            v-if="citationFilterUrl"
            class="ai-visibility-questions-tab__citation-filter-url"
          >
            {{ citationFilterUrl }}
          </span>
        </div>
        <button
          type="button"
          @click="clearCitationQuestionFilter"
        >
          <i class="material-icons-round">close</i>
          Clear filter
        </button>
      </div>
      <questions-table
        :rows="paginatedRows"
        :pagination-summary="paginationSummary"
        :page="currentPage"
        :page-count="pageCount"
        :index-offset="paginationOffset"
        @update:page="currentPage = $event"
        @select-question="openQuestionSidebar"
        @stage-remove-question="stageRemoveQuestion"
        @undo-remove-question="undoRemoveQuestion"
        @remove-pending-question="removePendingQuestion"
      />

      <div
        v-if="hasPendingChanges"
        class="ai-visibility-questions-tab__change-bar"
      >
        <div class="change-inner">
          <div class="change-sum">
            Staged changes:
            <template v-if="pendingAddCount">
              <b>{{ pendingAddCount }}</b> to add
            </template>
            <template v-if="pendingAddCount && pendingRemoveCount">
              ·
            </template>
            <template v-if="pendingRemoveCount">
              <b>{{ pendingRemoveCount }}</b> to remove
            </template>
          </div>
          <div class="change-cost">
            <i class="mdi mdi-database-outline"></i>
            Applying re-runs this report · <b>{{ changeCostLabel }}</b>
          </div>
          <span v-if="actionError" class="ai-visibility-questions-tab__change-error">
            {{ actionError }}
          </span>
          <div class="change-actions">
            <button
              type="button"
              class="btn btn-ghost"
              :disabled="applyingChanges"
              @click="discardQuestionChanges"
            >
              Discard
            </button>
            <button
              type="button"
              class="btn btn-primary"
              :disabled="isApplyDisabled"
              @click="applyQuestionChanges"
            >
              Apply changes & re-run
            </button>
          </div>
        </div>
      </div>

      <question-details-sidebar
        v-if="selectedQuestion"
        :open="isSidebarOpen"
        :question="selectedQuestion"
        :tracked-domain="detail.domain"
        @close="closeQuestionSidebar"
        @after-close="clearSelectedQuestion"
      />
    </div>
  </section>
</template>

<script>
import { createNewTrackerWithQuestions, getAiVisibilityQuestions } from '../../../api'
import { buildQuestionFilterCounts, buildQuestionSummary, createEmptyQuestionsData, normalizeQuestionRows, questionMatchesSearch, QUESTION_FILTERS, sortQuestionsByOpportunity } from './questionsData'
import LoadingState from '../../ui/states/LoadingState.vue'
import ErrorState from '../../ui/states/ErrorState.vue'
import EmptyState from '../../ui/states/EmptyState.vue'
import QuestionSummaryBar from './components/QuestionSummaryBar.vue'
import QuestionsToolbar from './components/QuestionsToolbar.vue'
import QuestionsTable from './components/QuestionsTable.vue'
import QuestionDetailsSidebar from './components/QuestionDetailsSidebar.vue'

const PAGE_SIZE = 10

export default {
  name: 'AiVisibilityQuestionsTab',
  components: {
    LoadingState,
    ErrorState,
    EmptyState,
    QuestionSummaryBar,
    QuestionsToolbar,
    QuestionsTable,
    QuestionDetailsSidebar,
  },
  props: {
    siteId: {
      type: [Number, String],
      required: true,
    },
    trackerId: {
      type: [Number, String],
      required: true,
    },
    detail: {
      type: Object,
      required: true,
    },
    filters: {
      type: Object,
      required: true,
    },
    filterOptions: {
      type: Object,
      default () {
        return {}
      },
    },
    embedded: {
      type: Boolean,
      default: false,
    },
    embeddedQuery: {
      type: Object,
      default () {
        return {}
      },
    },
  },
  data () {
    return {
      loading: false,
      loadError: '',
      questionsData: createEmptyQuestionsData(),
      search: '',
      activeFilter: 'everything',
      currentPage: 1,
      selectedQuestionId: null,
      isSidebarOpen: false,
      baseSelectedQuestionIds: [],
      pendingAddQuestions: [],
      pendingRemovedQuestionIds: [],
      pendingCustomQuestions: [],
      creditPreview: null,
      applyingChanges: false,
      actionError: '',
    }
  },
  computed: {
    normalizedRows () {
      return sortQuestionsByOpportunity(normalizeQuestionRows(this.questionsData.questions || []))
    },
    displayRows () {
      return this.normalizedRows.map((row) => ({
        ...row,
        pendingRemove: this.pendingRemovedQuestionIds.includes(row.questionId),
      })).concat(this.pendingAddRows)
    },
    pendingAddRows () {
      if (this.hasQuestionIdFilter) {
        return []
      }

      return this.pendingAddQuestions.map((question) => this.createPendingAddRow(question))
    },
    routeQuery () {
      return this.embedded ? this.embeddedQuery : this.$route.query
    },
    questionIdFilter () {
      const rawQuestionIds = this.routeQuery.questionIds
      const values = Array.isArray(rawQuestionIds) ? rawQuestionIds : [rawQuestionIds]

      return values
        .filter(value => value !== null && value !== undefined && value !== '')
        .reduce((questionIds, value) => {
          String(value).split(',').forEach((questionId) => {
            const normalizedId = questionId.trim()
            if (normalizedId && !questionIds.includes(normalizedId)) {
              questionIds.push(normalizedId)
            }
          })
          return questionIds
        }, [])
    },
    questionIdFilterSet () {
      return new Set(this.questionIdFilter)
    },
    hasQuestionIdFilter () {
      return Object.prototype.hasOwnProperty.call(this.routeQuery, 'questionIds')
    },
    questionScopedRows () {
      if (!this.hasQuestionIdFilter) {
        return this.displayRows
      }

      return this.displayRows.filter(row => (
        this.questionIdFilterSet.has(String(row.questionId))
      ))
    },
    summary () {
      return buildQuestionSummary(this.effectiveRows)
    },
    filterCounts () {
      return buildQuestionFilterCounts(this.effectiveRows)
    },
    effectiveRows () {
      return this.displayRows.filter((row) => !row.pendingRemove)
    },
    filteredRows () {
      return this.questionScopedRows.filter((row) => {
        if (this.activeFilter !== 'everything' && row.status !== this.activeFilter) {
          return false
        }

        return questionMatchesSearch(row, this.search)
      })
    },
    pageCount () {
      return Math.max(1, Math.ceil(this.filteredRows.length / PAGE_SIZE))
    },
    paginatedRows () {
      const startIndex = (this.currentPage - 1) * PAGE_SIZE
      return this.filteredRows.slice(startIndex, this.currentPage * PAGE_SIZE)
    },
    paginationOffset () {
      return (this.currentPage - 1) * PAGE_SIZE
    },
    paginationStart () {
      if (!this.filteredRows.length) {
        return 0
      }

      return ((this.currentPage - 1) * PAGE_SIZE) + 1
    },
    paginationEnd () {
      return Math.min(this.currentPage * PAGE_SIZE, this.filteredRows.length)
    },
    paginationSummary () {
      if (!this.filteredRows.length) {
        return 'Showing 0 questions'
      }

      return `Showing ${this.paginationStart}-${this.paginationEnd} of ${this.filteredRows.length} questions`
    },
    citationFilterSummary () {
      const questionLabel = this.questionScopedRows.length === 1 ? 'question' : 'questions'

      if (this.citationFilterUrl) {
        return `Showing ${this.questionScopedRows.length} ${questionLabel} that use this citation.`
      }

      return `Showing ${this.questionScopedRows.length} ${questionLabel} linked to the selected citation.`
    },
    citationFilterUrl () {
      return String(this.routeQuery.citationUrl || '').trim()
    },
    selectedQuestion () {
      if (this.selectedQuestionId == null) {
        return null
      }

      return this.normalizedRows.find(row => row.questionId === this.selectedQuestionId) || null
    },
    hasPendingChanges () {
      return this.pendingAddQuestions.length > 0 || this.pendingRemovedQuestionIds.length > 0
    },
    finalSelectedQuestionIds () {
      const removedIds = new Set(this.pendingRemovedQuestionIds)
      const selectedIds = this.baseSelectedQuestionIds.filter((id) => !removedIds.has(id))

      this.pendingAddQuestions.forEach((question) => {
        if (!selectedIds.includes(question.id)) {
          selectedIds.push(question.id)
        }
      })

      return selectedIds
    },
    finalQuestionCount () {
      return this.finalSelectedQuestionIds.length
    },
    pendingAddCount () {
      return this.pendingAddQuestions.length
    },
    pendingRemoveCount () {
      return this.pendingRemovedQuestionIds.length
    },
    changeCostLabel () {
      if (!this.creditPreview) {
        return 'Cost unavailable'
      }

      const preview = this.creditPreview
      const baseCredits = Number(preview.baseRefreshCredits || 0)
      const creditsPerQuestion = Number(preview.creditsPerQuestion || 0)
      const unitLabel = preview.unitLabel || 'POP credit'
      const requiredCredits = baseCredits + (this.finalQuestionCount * creditsPerQuestion)
      const unitText = requiredCredits === 1 ? unitLabel : `${unitLabel}s`

      return `${requiredCredits} ${unitText}`
    },
    isApplyDisabled () {
      return this.applyingChanges || !this.hasPendingChanges || this.finalQuestionCount === 0
    },
  },
  watch: {
    siteId: 'loadQuestions',
    trackerId () {
      this.discardQuestionChanges()
      this.loadQuestions()
    },
    'filters.range': 'loadQuestions',
    'filters.model': 'loadQuestions',
    routeQuery: {
      handler () {
        this.syncRouteStatusFilter()
        this.currentPage = 1
        this.closeQuestionSidebar()
      },
      deep: true,
    },
    pageCount (pageCount) {
      if (this.currentPage > pageCount) {
        this.currentPage = pageCount
      }
    },
  },
  beforeMount () {
    this.syncRouteStatusFilter()
    this.loadQuestions()
  },
  methods: {
    loadQuestions () {
      this.loading = true
      this.loadError = ''
      this.closeQuestionSidebar()

      return getAiVisibilityQuestions(this.siteId, this.trackerId, this.filters)
        .then((response) => {
          const payload = response.data || {}
          this.questionsData = payload || createEmptyQuestionsData()
          this.creditPreview = payload.creditPreview || null
          this.currentPage = 1
        })
        .catch(() => {
          this.questionsData = createEmptyQuestionsData()
          this.creditPreview = null
          this.loadError = 'Could not load AI Visibility questions data.'
        })
        .finally(() => {
          this.loading = false
        })
    },
    stageRemoveQuestion (row) {
      if (!row || row.questionId == null || row.pendingAdd) {
        return
      }

      if (!this.baseSelectedQuestionIds.length) {
        this.baseSelectedQuestionIds = this.normalizedRows.map((question) => question.questionId)
      }

      if (!this.pendingRemovedQuestionIds.includes(row.questionId)) {
        this.pendingRemovedQuestionIds = this.pendingRemovedQuestionIds.concat(row.questionId)
      }
      this.actionError = ''
      this.closeQuestionSidebar()
    },
    undoRemoveQuestion (row) {
      if (!row || row.questionId == null) {
        return
      }

      this.pendingRemovedQuestionIds = this.pendingRemovedQuestionIds.filter((id) => id !== row.questionId)
      this.actionError = ''
    },
    removePendingQuestion (row) {
      if (!row || row.questionId == null) {
        return
      }

      this.pendingAddQuestions = this.pendingAddQuestions.filter((question) => question.id !== row.questionId)
      this.actionError = ''
    },
    discardQuestionChanges () {
      this.baseSelectedQuestionIds = []
      this.pendingAddQuestions = []
      this.pendingRemovedQuestionIds = []
      this.pendingCustomQuestions = []
      this.creditPreview = null
      this.actionError = ''
      this.applyingChanges = false
    },
    reloadUser () {
      return this.$store.dispatch('loadUser').then((response) => {
        this.$store.commit('setUser', response.data.user)
      })
    },
    applyQuestionChanges () {
      if (this.isApplyDisabled) {
        return Promise.resolve()
      }

      this.applyingChanges = true
      this.actionError = ''

      return createNewTrackerWithQuestions(
        this.siteId,
        this.trackerId,
        {
          selectedQuestionIds: this.finalSelectedQuestionIds,
          customQuestions: this.pendingCustomQuestions,
        },
      )
        .then((response) => {
          return this.reloadUser().then(() => {
            this.discardQuestionChanges()
            this.$emit('tracker-created', response.data)
          })
        })
        .catch(() => {
          this.actionError = 'Could not update tracker questions.'
        })
        .finally(() => {
          this.applyingChanges = false
        })
    },
    createPendingAddRow (question) {
      return {
        questionId: question.id,
        questionText: question.text || '',
        tableMetaLabel: 'Will start tracking on next run',
        status: 'unknown',
        statusLabel: 'Pending first run',
        statusTone: 'neutral',
        rankSupportLabel: '',
        yourVisibilityScore: {
          current: 0,
          change: null,
        },
        rankLabel: '-',
        rankDenominatorLabel: '',
        trend: null,
        askedCount: 0,
        searchableText: question.text || '',
        pendingAdd: true,
      }
    },
    handleSearchUpdate (value) {
      this.search = value
      this.currentPage = 1
    },
    handleFilterChange (value) {
      this.activeFilter = value
      this.currentPage = 1
    },
    syncRouteStatusFilter () {
      const status = this.routeQuery.status
      if (!this.isValidFilter(status)) {
        this.activeFilter = 'everything'
        this.currentPage = 1
        return
      }

      this.activeFilter = status
      this.currentPage = 1
    },
    isValidFilter (value) {
      return QUESTION_FILTERS.some(filter => filter.value === value)
    },
    clearCitationQuestionFilter () {
      const query = { ...this.routeQuery }
      delete query.questionIds
      delete query.citationUrl
      delete query.status

      if (this.embedded) {
        this.$emit('update-query', query)
        return
      }
      this.$router.push({
        name: this.$route.name,
        params: this.$route.params,
        query,
      })
    },
    openQuestionSidebar (row) {
      if (!row || row.questionId == null) {
        return
      }

      this.selectedQuestionId = row.questionId
      this.$nextTick(() => {
        this.isSidebarOpen = true
      })
    },
    closeQuestionSidebar () {
      this.isSidebarOpen = false
    },
    clearSelectedQuestion () {
      this.selectedQuestionId = null
    },
  },
}
</script>

<style lang="scss" scoped>

.ai-visibility-questions-tab {
  margin-top: 24px;
  padding-bottom: 30px;
}

.ai-visibility-questions-tab__content {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.ai-visibility-questions-tab__change-bar {
  position: sticky;
  z-index: 5;
  bottom: 18px;
  border: 1px solid rgba(255, 87, 0, 0.28);
  border-radius: 8px;
  background: #012f34;
  box-shadow: 0 10px 28px rgba(16, 24, 40, 0.14);
  padding: 0;
  width: 75%;
  margin: auto;
}

.change-inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 14px 16px;
}

.change-sum {
  color: var(--v-whiteColor-base);
  font-size: 15px;
  line-height: 1.3;
}

.change-sum b,
.change-cost b {
  font-weight: 800;
}

.change-cost {
  display: inline-flex;
  min-width: 0;
  align-items: center;
  gap: 6px;
  color: rgba(255, 255, 255, .72);
  font-size: 13px;
  line-height: 1.3;
      padding-left: 22px;
    border-left: 1px solid rgba(255, 255, 255, .16);

  b {
    color: #ffffff;
  }
}

.change-cost i {
  flex: 0 0 auto;
  font-size: 16px;
}

.ai-visibility-questions-tab__change-error {
  color: var(--v-red11Color-base) !important;
  font-size: 13px;
  line-height: 1.35;
}

.change-actions {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 10px;
}

.change-actions .btn {
  display: inline-flex;
  min-height: 38px;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  padding: 8px 16px;
}

.change-actions .btn-ghost {
  border: 1px solid rgba(255, 255, 255, .35);
  background: transparent;
  color: rgba(255, 255, 255, .82);

  &:hover {
    background-color: rgba(255, 255, 255, .08);
  }
}

.change-actions .btn-primary {
  border: 1px solid var(--v-profileAnchorColor-base);
  background: var(--v-profileAnchorColor-base);
  color: var(--v-cardBg-base);

  &:hover {
    background-color: #E64E00;
  }

}

.change-actions .btn:disabled {
  cursor: not-allowed;
  opacity: 0.55;
}

.ai-visibility-questions-tab__citation-filter {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  border: 1px solid rgba(255, 87, 0, 0.28);
  border-radius: 8px;
  background: rgba(255, 87, 0, 0.10);
  box-shadow: inset 3px 0 0 var(--v-profileAnchorColor-base);
  padding: 14px 16px;
}

.ai-visibility-questions-tab__citation-filter div {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 4px;
}

.ai-visibility-questions-tab__citation-filter strong {
  color: var(--v-mainColor-base);
  font-size: 14px;
  font-weight: 700;
  line-height: 1.3;
}

.ai-visibility-questions-tab__citation-filter span {
  color: var(--v-mainColor3-base);
  font-size: 14px;
  line-height: 1.4;
}

.ai-visibility-questions-tab__citation-filter-url {
  display: block;
  max-width: 100%;
  color: var(--v-mainColor-base) !important;
  font-weight: 600;
  overflow-wrap: anywhere;
}

.ai-visibility-questions-tab__citation-filter button {
  display: inline-flex;
  min-height: 34px;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  gap: 6px;
  border: 1px solid var(--v-profileAnchorColor-base);
  border-radius: 20px;
  background: var(--v-cardBg-base);
  color: var(--v-profileAnchorColor-base);
  cursor: pointer;
  font-size: 14px;
  font-weight: 700;
  padding: 6px 12px;
  white-space: nowrap;
}

.ai-visibility-questions-tab__citation-filter button i {
  font-size: 18px;
}

.ai-visibility-questions-tab__citation-filter button:focus {
  outline: 2px solid var(--v-lightYellowColor-base);
  outline-offset: 2px;
}

@media (max-width: 760px) {
  .ai-visibility-questions-tab__change-bar {
    width: 100%;
  }

  .change-inner {
    align-items: flex-start;
    flex-direction: column;
  }

  .change-actions {
    width: 100%;
    justify-content: flex-end;
  }

  .ai-visibility-questions-tab__citation-filter {
    align-items: flex-start;
    flex-direction: column;
  }
}

</style>
